import { Suspense, useEffect } from 'react';
import { documentationPanelType } from '../../../../documentation/types';
import { PanelConsumer } from '../../../../panels/PanelsContext';
import { useConfigurationCanvas } from '../../../configuration-canvas/public/useConfigurationCanvas';
import DocumentationDialog from '../../../../documentation/DocumentationDialog';
import { ConnectedNewSfFunnelDialog } from '../../../funnel-map-flow/new-sf-funnel-dialog/NewSfFunnelDialog';
import { FunnelMapPluginPanels } from '../../../funnel-map-flow/templates/funnel-map-plugins/FunnelMapPluginPanels';
import { CanvasUnitedTemplatesDialog } from './CanvasUnitedTemplatesDialog';
import { automationsPanelType, playbookAlertsPanelType } from '../../../../Automations/panelTypes';
import { ConfigurationCanvasAutomationsPanel } from '../../CanvasAutomationsPanel';
import { useSelector } from 'react-redux';
import { selectCrmOrgAlerts } from '../../../../../reducers/global/alertsReducers';
import { selectCrmOrgAutomations } from '../../../../../reducers/global/automationReducers';
import { importFunnelPanelType } from '../../../funnel-map-flow/dialogs/import-funnel/importFunnelPanelType';
import { ImportFunnelWrapper } from '../../../funnel-map-flow/dialogs/import-funnel/ImportFunnelWrapper';
import { metadataElementsPanelType } from '../../../funnel-map-flow/dialogs/metadata-elements/metadataElementsPanelType';
import { MetadataElementsPanel } from '../../../funnel-map-flow/dialogs/metadata-elements/MetadataElementsPanel';
import { dependenciesPanelType } from '../../../funnel-map-flow/dialogs/dependencies/dependenciesPanelType';
import { DependenciesPanel } from '../../../funnel-map-flow/dialogs/dependencies/DependenciesPanel';
import {
  selectStageDialogStage,
  selectStageDialogFunnelId,
} from '../../../../../reducers/funnel-flow-page/stageDialogReducer';
import { stagePanelType } from '../../../funnel-map-flow/dialogs/stage/panelType';
import { StageDialog } from '../../../funnel-map-flow/dialogs/stage/StageDialog';
import ConnectedFunnelDialog from '../../../funnel-map-flow/dialogs/funnel-panel/ConnectedFunnelDialog';
import { funnelPanelType } from '../../../funnel-map-flow/dialogs/funnel-panel/funnelPanelTypes';
import { ConnectedGateDialog } from '../../../funnel-map-flow/dialogs/gates/ConnectedGateDialog';
import { gatePanelType } from '../../../funnel-map-flow/dialogs/gates/gatePanelType';
import DeprecatedFieldsManagementDialog from '../../../funnel-map-flow/dialogs/deprecated-fields-management/FieldsManagementDialog';
import FieldsManagementDialog from '../../../funnel-map-flow/dialogs/fields-management/FieldsManagementDialog';
import { selectIsFieldsDialogOpen } from '../../../../../reducers/funnel-flow-page/fieldsReducer';
import { ConfigurationCanvasLeftPanel } from '../../../configuration-canvas-panel';
import { WithEnvConnectionErrorPanel } from './error-panel/EnvConnectionErrorPanel';
import { AutomationAlertsContextProvider } from '../../../../alerts/AutomationAlertsContextProvider';
import { AutomationAutomationsContextProvider } from '../../../../alerts/AutomationAutomationsContextProvider';
import { aiAgentCenterPanelType } from '../../../../dashboard/aiAgents/aiAgentCenterPanelType';
import { CenteredCircularProgress } from '../../../../common/CenteredCircularProgress';
import AgentsHomePanel from '../../../../aiAgentCenter/AgentsHomePanel';
import { useFeatureToggle } from '../../../../../feature-flags/useFeatureToggle';
import useQueryParams from '../../../../../navigation-utils/useQueryParams';
import { DeepLinkSearchParams } from '@server/deep-link-search-params';
import { AutomationType } from '../../../../../types/enums/AutomationType';
import { usePanels } from '../../../../panels/usePanels';
import {
  PanelEnvSelector,
  panelEnvSelectorHack,
} from '../../../environments/connect-env/PanelEnvSelector';
import { selectDefaultCreationCrmOrgId } from '../../../../../reducers/userInfoReducer';
import { metadataMappingPanelType } from '../process-mining/metadataMappingPanelType';
import { MetadataMappingChatPanel } from '../process-mining/MetadataMappingChatPanel';
import { ConfigurationCanvasRightPanel } from '../../../configuration-canvas-panel/ConfigurationCanvasRightPanel';
import { useMetadataMoreCtx } from '../../../funnel-map-flow/dialogs/metadata-elements/more-menu/MetatadataMoreCtx';
import { ConnectedMetadataMorePanel } from '../../../funnel-map-flow/dialogs/metadata-elements/more-menu/ConnectedMetadataMorePanel';

const useHandleDeepLink = () => {
  const query = useQueryParams();
  const { maybeOpenPanel } = usePanels();

  useEffect(() => {
    const automationType = query.get(DeepLinkSearchParams.automationType);
    if (automationType) {
      switch (automationType) {
        case AutomationType.Default:
          maybeOpenPanel({
            panelType: automationsPanelType,
          });
          break;
        case AutomationType.Assignment:
          maybeOpenPanel({
            panelType: playbookAlertsPanelType,
          });
          break;
        case AutomationType.Alert:
          maybeOpenPanel({
            panelType: playbookAlertsPanelType,
          });
          break;
        case AutomationType.Dedupe:
          break;
        case AutomationType.Matching:
          break;
      }
    }
    const objectName = query.get(DeepLinkSearchParams.objectName);
    if (objectName) {
      maybeOpenPanel({
        panelType: documentationPanelType,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const CanvasUnitedFlow = () => {
  const { canvasFunnelMap, canvasCrmOrgId } = useConfigurationCanvas();
  const { sfFieldFieldsDialog } = useFeatureToggle();
  useHandleDeepLink();

  const automations = useSelector(selectCrmOrgAutomations(canvasCrmOrgId));
  const alerts = useSelector(selectCrmOrgAlerts(canvasCrmOrgId));
  const selectedStage = useSelector(selectStageDialogStage);
  const selectedStageDialogFunnelId = useSelector(selectStageDialogFunnelId);
  const isFieldsManagementDialogOpen = useSelector(selectIsFieldsDialogOpen);
  const { metadataMorePanelState, handleMetadataMoreClose } = useMetadataMoreCtx();

  const currentCrmOrgId = useSelector(selectDefaultCreationCrmOrgId);

  return (
    <>
      <PanelConsumer panelType={documentationPanelType}>
        {({ isOpen, maybeClosePanel }) => (
          <>
            {isOpen && (
              <ConfigurationCanvasLeftPanel withExpandedModeButton sx={panelEnvSelectorHack}>
                <WithEnvConnectionErrorPanel shouldWaitForInitialFetch={true}>
                  <Suspense fallback={<CenteredCircularProgress />}>
                    <PanelEnvSelector filterByEnvsUsedInCanvas />
                    <DocumentationDialog
                      crmOrgId={canvasCrmOrgId}
                      onCloseDialog={() => maybeClosePanel()}
                      hideAddObjectButton
                      key={currentCrmOrgId} // force re-render when the current crm org changes
                    />
                  </Suspense>
                </WithEnvConnectionErrorPanel>
              </ConfigurationCanvasLeftPanel>
            )}
          </>
        )}
      </PanelConsumer>
      <CanvasUnitedTemplatesDialog />
      <FunnelMapPluginPanels isReadOnly={false} />
      <ConnectedNewSfFunnelDialog hideUseFieldsFrom />

      <PanelConsumer panelType={automationsPanelType}>
        {({ isOpen, maybeClosePanel }) => (
          <>
            {isOpen && (
              <AutomationAutomationsContextProvider>
                <ConfigurationCanvasAutomationsPanel
                  automations={automations}
                  onClose={maybeClosePanel}
                  disableCanvasTemplates
                />
              </AutomationAutomationsContextProvider>
            )}
          </>
        )}
      </PanelConsumer>
      <PanelConsumer panelType={playbookAlertsPanelType}>
        {({ isOpen, maybeClosePanel }) => (
          <>
            {isOpen && (
              <AutomationAlertsContextProvider>
                <ConfigurationCanvasAutomationsPanel
                  automations={alerts}
                  onClose={maybeClosePanel}
                  disableCanvasTemplates
                />
              </AutomationAlertsContextProvider>
            )}
          </>
        )}
      </PanelConsumer>
      <PanelConsumer panelType={importFunnelPanelType}>
        {({ isOpen, maybeClosePanel }) =>
          isOpen && (
            <ImportFunnelWrapper
              onClose={() => maybeClosePanel()}
              crmOrgId={canvasCrmOrgId}
              key={currentCrmOrgId} // force re-render when the current crm org changes
            />
          )
        }
      </PanelConsumer>
      <PanelConsumer panelType={metadataElementsPanelType}>
        {({ isOpen, maybeClosePanel }) =>
          isOpen && (
            <MetadataElementsPanel
              onClose={() => maybeClosePanel()}
              crmOrgId={canvasCrmOrgId}
              key={currentCrmOrgId}
            />
          )
        }
      </PanelConsumer>
      <PanelConsumer panelType={dependenciesPanelType}>
        {({ isOpen, maybeClosePanel, data }) =>
          isOpen &&
          data && (
            <DependenciesPanel
              metadataElementId={data.metadataElementId}
              crmOrgId={data.crmOrgId}
              color={data.color}
              onClose={() => maybeClosePanel()}
              key={data.metadataElementId + data.crmOrgId}
              canvasFunnelMap={canvasFunnelMap}
            />
          )
        }
      </PanelConsumer>
      <PanelConsumer panelType={metadataMappingPanelType}>
        {({ isOpen, maybeClosePanel }) =>
          isOpen && (
            <ConfigurationCanvasRightPanel width={407} sx={{ paddingTop: 0 }}>
              <MetadataMappingChatPanel onClose={() => maybeClosePanel()} />
            </ConfigurationCanvasRightPanel>
          )
        }
      </PanelConsumer>
      {selectedStage && selectedStageDialogFunnelId && (
        <PanelConsumer panelType={stagePanelType} instanceId={selectedStage._stageId}>
          {({ maybeClosePanel, isOpen, setIsDirty }) => (
            <>
              {isOpen && (
                <StageDialog
                  readonly={false}
                  stage={selectedStage}
                  funnelId={selectedStageDialogFunnelId}
                  crmOrgId={canvasCrmOrgId}
                  closeDialog={() => maybeClosePanel()}
                  setIsDirty={setIsDirty}
                  funnelMap={canvasFunnelMap}
                />
              )}
            </>
          )}
        </PanelConsumer>
      )}
      <PanelConsumer panelType={funnelPanelType}>
        {({ maybeClosePanel, isOpen, data }) => (
          <>
            {data && (
              <ConnectedFunnelDialog
                closeDialog={() => maybeClosePanel()}
                isOpen={isOpen}
                readonly={false}
                data={data}
                crmOrgId={canvasCrmOrgId}
                funnelMapId={canvasFunnelMap.id}
              />
            )}
          </>
        )}
      </PanelConsumer>
      <PanelConsumer panelType={gatePanelType}>
        {({ maybeClosePanel, isOpen, setIsDirty, data }) => (
          <>
            {data && (
              <ConnectedGateDialog
                crmOrgId={canvasCrmOrgId}
                readonly={false}
                closeDialog={() => maybeClosePanel()}
                setIsDirty={setIsDirty}
                isOpen={isOpen}
                data={data}
              />
            )}
          </>
        )}
      </PanelConsumer>
      <PanelConsumer panelType={aiAgentCenterPanelType}>
        {({ maybeClosePanel }) => <AgentsHomePanel closePanel={maybeClosePanel} />}
      </PanelConsumer>
      {isFieldsManagementDialogOpen && !sfFieldFieldsDialog && (
        <DeprecatedFieldsManagementDialog
          crmOrgId={canvasCrmOrgId}
          funnelMapId={canvasFunnelMap.id}
          key={currentCrmOrgId} // force re-render when the current crm org changes
        />
      )}
      {isFieldsManagementDialogOpen && sfFieldFieldsDialog && (
        <FieldsManagementDialog
          crmOrgId={canvasCrmOrgId}
          funnelMapId={canvasFunnelMap.id}
          key={currentCrmOrgId}
        />
      )}
      {metadataMorePanelState && (
        <ConnectedMetadataMorePanel
          funnelMap={canvasFunnelMap}
          metadataMorePanelState={metadataMorePanelState}
          handleMetadataMoreClose={handleMetadataMoreClose}
        />
      )}
    </>
  );
};
